import { getFirebaseMessaging } from "../firebase/firebase";
import { getToken, onMessage } from "firebase/messaging";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../hooks/hooks";
import { addNotification } from "../redux/notificationSlice";
import { NotificationPayload } from "../types/notificationTypes";

export const useFirebaseMessaging = () => {
  const dispatch = useAppDispatch();
  const [messaging, setMessaging] = useState<ReturnType<typeof getFirebaseMessaging>>(null);

  // Initialize messaging when hook mounts
  useEffect(() => {
    const checkMessaging = async () => {
      const currentMessaging = getFirebaseMessaging();
      if (!currentMessaging) {
        // If null, wait a moment in case initialization is still in progress
        await new Promise(resolve => setTimeout(resolve, 500));
        setMessaging(getFirebaseMessaging());
      } else {
        setMessaging(currentMessaging);
      }
    };

    checkMessaging();
  }, []);

  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        if (!messaging) {
          console.error("Firebase messaging is not initialized.");
          return null;
        }

        const token = await getToken(messaging, {
          vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
        });
        return token;
      }
    } catch (error) {
      console.error("Notification permission error:", error);
    }
    return null;
  };

  // Set up message listener
  useEffect(() => {
    if (!messaging) return;

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log("*********Message received. ", payload);
      const notificationPayload: NotificationPayload = {
        id: Date.now().toString(),
        title: payload.notification?.title || "New notification",
        message: payload.notification?.body || "",
        type: "info",
        data: payload.data,
        timestamp: new Date().toISOString(),
      };
      dispatch(addNotification(notificationPayload));
    });

    return () => unsubscribe();
  }, [dispatch, messaging]);

  return { requestNotificationPermission };
};